<script setup lang="ts">
// ================================
// PAGE D'AIDE « Comprendre l'étude » DE L'OPTIMISATION DU TURPE, NOUVELLE
// VERSION (DEV-2311 ; v0.6 du 19/10/2026, contrat de l'interface v1.3, §9).
//
// Ce que le client doit vivre : depuis son étude, il ouvre une page courte :
// « L'essentiel » ouvert (et « Ce qui change avec la nouvelle version »
// jusqu'au 31/12/2026), tout le reste replié derrière un titre porteur, à un
// clic ; venu d'un compteur C5, C4 ou C2, il ne lit que ce qui vaut pour ce
// segment (paramètre « segment » de l'adresse, jamais un PDL ni un site) ;
// un lien d'ancre ouvre la section qu'il vise ; le retour ramène à son étude.
// La page n'appelle aucun service : son contenu est statique
// (`optim-turpe-v2.aide.contenu.ts`, généré). Elle vit derrière la même garde
// que l'étude (building.routes.ts).
// ================================
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import {
    DATE_MISE_A_JOUR,
    INTRO_HTML,
    SECTIONS,
    VERSION,
} from "@modules/courtage/utils/optim-turpe-v2.aide.contenu"
import {
    sectionsDuSegment,
    sectionsOuvertesAuRepos,
    segmentDeLAdresse,
} from "@modules/courtage/utils/optim-turpe-v2.aide.segment"
import {
    NOMS_OPTIMTURPE_V2_OUVERTE,
    routeV2Query,
} from "@modules/courtage/utils/optimturpe-v2.acces"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            title: "Comprendre l'étude",
            retour: "Retour à l'étude",
            sommaire: "Sommaire",
            pied: "Version {version} · dernière mise à jour le {date}.",
        },
    },
})

const route = useRoute()
const router = useRouter()

/** Le retour mène à l'étude du même bâtiment, sur le compteur d'où l'on vient. */
const getRouteEtude = computed(() => {
    const batiment = route.params._id_building
    // Sans bâtiment dans l'adresse (montage hors de sa route), pas de lien :
    // la route nommée ne se résout pas sans son paramètre.
    if (typeof batiment !== "string" || !batiment) return null
    const idsource = route.query.idsource
    return {
        name: "PageBuildingOptimTurpeV2",
        params: { _id_building: batiment },
        query: routeV2Query(typeof idsource === "string" ? idsource : null),
    }
})

/** Les tableaux défilent dans leur propre conteneur, comme dans la maquette. */
function envelopperTableaux(html: string): string {
    return html.replace(
        /(<table\b[\s\S]*?<\/table>)/g,
        '<div class="defile">$1</div>'
    )
}

/** N7 : le segment du compteur d'où l'on vient ; `null` : la page générale. */
const getSegment = computed(() => segmentDeLAdresse(route.query.segment))

const getSections = computed(() =>
    sectionsDuSegment(SECTIONS, getSegment.value).map((s, i) => ({
        ...s,
        numero: i + 1,
        corps: envelopperTableaux(s.html),
    }))
)

// ================================
// REPLI (S0, D11) : une section par panneau ; une ancre ouvre sa section.
// ================================
const ouvertes = ref<string[]>(sectionsOuvertesAuRepos(new Date()))

function idDeLAncre(hash: string | null | undefined): string | null {
    const id = (hash ?? "").replace(/^#/, "")
    return getSections.value.some((s) => s.id === id) ? id : null
}

async function ouvrir(id: string, defiler: boolean): Promise<void> {
    if (!ouvertes.value.includes(id)) ouvertes.value = [...ouvertes.value, id]
    if (!defiler) return
    await nextTick()
    const cible = document.getElementById(id)
    if (!cible?.scrollIntoView) return
    // Le panneau s'ouvre par une transition de hauteur : tant qu'elle court,
    // la page n'a pas sa hauteur finale et le défilement s'arrête trop tôt
    // (mesuré dans le navigateur, 19/10). On suit l'ouverture jusqu'à 800 ms.
    const fin = performance.now() + 800
    const suivre = () => {
        cible.scrollIntoView({ block: "start" })
        if (performance.now() < fin) requestAnimationFrame(suivre)
    }
    suivre()
}

/** Un clic sur un lien interne (sommaire ou renvoi du texte) ouvre la section visée. */
function onClicGrille(evenement: MouseEvent): void {
    const lien = (evenement.target as HTMLElement | null)?.closest?.(
        'a[href^="#"]'
    )
    const id = idDeLAncre(lien?.getAttribute("href"))
    if (!id) return
    // La section s'ouvre, puis vient en haut de l'écran ; l'adresse garde
    // l'ancre (lien à partager), sans nouvelle entrée d'historique.
    evenement.preventDefault()
    void router.replace({ ...route, hash: `#${id}` }).catch(() => undefined)
    void ouvrir(id, true)
}

watch(
    () => route.hash,
    (hash) => {
        const id = idDeLAncre(hash)
        if (id) void ouvrir(id, true)
    }
)

// ================================
// SOMMAIRE : la section visible est marquée (comme le gabarit de la maquette).
// ================================
const sectionCourante = ref<string>(getSections.value[0]?.id ?? "")
const contenu = ref<HTMLElement | null>(null)
let observateur: IntersectionObserver | null = null

onMounted(async () => {
    const id = idDeLAncre(route.hash)
    if (id) await ouvrir(id, true)
    await nextTick()
    const cartes = Array.from(
        contenu.value?.querySelectorAll<HTMLElement>(
            '[data-testid="optim-turpe-v2-aide-section"]'
        ) ?? []
    )
    if (typeof IntersectionObserver === "undefined" || !cartes.length) return
    observateur = new IntersectionObserver(
        (entrees) => {
            const visibles = entrees
                .filter((e) => e.isIntersecting)
                .sort(
                    (a, b) =>
                        a.boundingClientRect.top - b.boundingClientRect.top
                )
            if (visibles.length) sectionCourante.value = visibles[0].target.id
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    )
    cartes.forEach((c) => observateur?.observe(c))
})

onBeforeUnmount(() => {
    observateur?.disconnect()
    observateur = null
})
</script>

<template>
    <div class="page page-building-optim-turpe-v2-aide">
        <HeaderPageBuilding :title="t('title')" class-header="aide-entete">
            <div class="aide-entete-ligne">
                <p class="fil" data-testid="optim-turpe-v2-aide-fil">
                    {{ NOMS_OPTIMTURPE_V2_OUVERTE.entreeV2 }}
                </p>
                <router-link
                    v-if="getRouteEtude"
                    :to="getRouteEtude"
                    class="text-xs font-medium text-primary-700 hover:underline"
                    data-testid="optim-turpe-v2-aide-retour"
                    >{{ t("retour") }}</router-link
                >
            </div>
        </HeaderPageBuilding>

        <div class="grille" @click="onClicGrille">
            <!-- Contenu statique écrit par l'équipe (aucune donnée d'utilisateur),
                 généré depuis la maquette validée : v-html est sûr ici. -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="chapeau" v-html="INTRO_HTML" />

            <nav class="sommaire" :aria-label="t('sommaire')">
                <p class="etiquette">{{ t("sommaire") }}</p>
                <ol>
                    <li v-for="s in getSections" :key="s.id">
                        <a
                            :href="`#${s.id}`"
                            :aria-current="
                                sectionCourante === s.id ? 'true' : 'false'
                            "
                            data-testid="optim-turpe-v2-aide-sommaire-lien"
                            ><span class="n">{{ s.numero }}</span
                            ><span>{{ s.court }}</span></a
                        >
                    </li>
                </ol>
            </nav>

            <div ref="contenu" class="contenu">
                <BaseAccordion
                    v-model:value="ouvertes"
                    multiple
                    class="sections"
                >
                    <BaseAccordionPanel
                        v-for="s in getSections"
                        :id="s.id"
                        :key="s.id"
                        :value="s.id"
                        class="carte"
                        :data-ouvert="ouvertes.includes(s.id) ? '1' : '0'"
                        data-testid="optim-turpe-v2-aide-section"
                    >
                        <BaseAccordionHeader>
                            <span class="carte-entete">
                                <span class="n">{{ s.numero }}</span>
                                <span
                                    class="titre"
                                    role="heading"
                                    aria-level="2"
                                    data-testid="optim-turpe-v2-aide-section-titre"
                                    >{{ s.titre }}</span
                                >
                            </span>
                        </BaseAccordionHeader>
                        <BaseAccordionContent>
                            <!-- Même justification que le chapeau : HTML
                                 statique de l'équipe, restreint aux balises
                                 de texte (et aux replis details/summary des
                                 questions fréquentes). -->
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <div class="carte-corps" v-html="s.corps" />
                        </BaseAccordionContent>
                    </BaseAccordionPanel>
                </BaseAccordion>
                <p class="pied" data-testid="optim-turpe-v2-aide-pied">
                    {{
                        t("pied", { version: VERSION, date: DATE_MISE_A_JOUR })
                    }}
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* ---------------------------------------------------------------
   Règles du gabarit de la maquette v0.4, avec les jetons de
   l'application (échelle bleue et gris du thème, `server/style.css`).
   Une seule échelle d'espacement : 4, 8, 12, 16, 20, 24, 32.
   Deux encres n'ont pas d'équivalent dans le thème : leur valeur est
   celle de la maquette.
   --------------------------------------------------------------- */
.page-building-optim-turpe-v2-aide {
    --carte: #ffffff;
    --carte-2: var(--color-slate-50, #fafafa);
    --trait: var(--color-slate-200, #eeeeee);
    --trait-2: var(--color-slate-300, #e0e0e0);
    --titre: var(--color-blue-700, #151c5d);
    --encre: var(--color-blue-900, #0c4a6e);
    --encre-2: var(--color-blue-800, #075985);
    --encre-3: #0369a1;
    --encre-4: #4a6f8a;
    --puce: var(--color-blue-400, #6586d5);
    --accent: var(--color-blue-700, #151c5d);
    --accent-fond: var(--color-blue-50, #f0f9ff);
    --accent-trait: var(--color-blue-200, #bae6fd);
    --s1: 4px;
    --s2: 8px;
    --s3: 12px;
    --s4: 16px;
    --s5: 20px;
    --s6: 24px;
    --s7: 32px;
    --rayon: 8px;
    --mesure: 72ch;
    --sommaire: 232px;
    /* Le début de la colonne de texte dans une carte : marge interne, colonne
       des numéros, bordure. L'en-tête et le chapeau s'y alignent. */
    --colonne-texte: calc(var(--s5) + var(--s7) + 1px);
    max-width: 1120px;
    color: var(--encre);
    font-size: 14px;
    line-height: 22px;
}

/* En-tête : le titre, le nom de l'étude et le retour, dans la colonne de texte. */
.page-building-optim-turpe-v2-aide :deep(.aide-entete) {
    padding-inline-start: calc(
        var(--sommaire) + var(--s6) + var(--colonne-texte)
    );
}
.aide-entete-ligne {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--s1) var(--s4);
    margin-top: var(--s1);
}
.fil {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--encre-3);
}

.grille {
    display: grid;
    grid-template-columns: var(--sommaire) minmax(0, 1fr);
    gap: var(--s6);
    align-items: start;
    margin-top: var(--s2);
}
.chapeau {
    grid-column: 2;
    grid-row: 1;
    max-width: var(--mesure);
    padding-inline-start: var(--colonne-texte);
    color: var(--encre-2);
}
.chapeau :deep(p) {
    margin: 0;
}
.chapeau :deep(p + p) {
    margin-top: var(--s2);
}
.chapeau :deep(a) {
    color: var(--encre-3);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.15em;
}

/* Sommaire : collant à gauche, une ligne par section, le numéro dans sa colonne. */
.sommaire {
    grid-column: 1;
    grid-row: 2;
    position: sticky;
    top: var(--s6);
    background: var(--carte);
    border: 1px solid var(--trait);
    border-radius: var(--rayon);
    padding: var(--s3) 0;
}
.etiquette {
    margin: 0 var(--s4) var(--s2);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--encre-3);
}
.sommaire ol {
    list-style: none;
    margin: 0;
    padding: 0;
}
.sommaire li a {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: var(--s2);
    padding: var(--s1) var(--s4);
    font-size: 13px;
    line-height: 24px;
    color: var(--encre);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.15em;
}
.sommaire li a:hover {
    background: var(--carte-2);
    text-decoration: none;
}
.sommaire li a[aria-current="true"] {
    box-shadow: inset 2px 0 0 var(--accent);
    background: var(--accent-fond);
    font-weight: 500;
}
.sommaire .n {
    color: var(--encre-4);
    font-variant-numeric: tabular-nums;
    text-align: right;
}

/* Sections : les cartes de l'étude (bord, blanc, 20 px de marge interne). */
.contenu {
    grid-column: 2;
    grid-row: 2;
    display: flex;
    flex-direction: column;
    gap: var(--s4);
    min-width: 0;
}
/* Une section = un panneau replié (S0) : le bord, le blanc et l'en-tête des
   cartes de la maquette ; l'en-tête est le bouton du panneau. */
.sections {
    display: flex;
    flex-direction: column;
    gap: var(--s4);
}
.carte {
    background: var(--carte);
    border: 1px solid var(--trait);
    border-radius: var(--rayon);
    scroll-margin-top: var(--s6);
    overflow: hidden;
}
.carte :deep(.p-accordionheader) {
    display: flex;
    align-items: center;
    gap: var(--s3);
    width: 100%;
    padding: var(--s3) var(--s5);
    background: var(--carte);
    border: 0;
    border-radius: 0;
    color: var(--titre);
    text-align: left;
}
/* Le chevron dit que le titre s'ouvre (en-tête porteur). */
.carte :deep(.p-accordionheader-toggle-icon) {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: var(--encre-3);
}
.carte :deep(.p-accordionheader:hover) {
    background: var(--carte-2);
}
.carte :deep(.p-accordionheader:focus-visible) {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
}
.carte[data-ouvert="1"] :deep(.p-accordionheader) {
    border-bottom: 1px solid var(--trait);
}
.carte :deep(.p-accordioncontent-content) {
    padding: 0;
    background: var(--carte);
    border: 0;
}
.carte-entete {
    display: grid;
    flex: 1;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: var(--s2);
    align-items: baseline;
}
.carte-entete .n {
    font-size: 14px;
    font-weight: 600;
    color: var(--encre-4);
    font-variant-numeric: tabular-nums;
    text-align: right;
}
.carte-entete .titre {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: var(--titre);
}
.carte-corps {
    padding: var(--s4) var(--s5) var(--s5) calc(var(--s5) + var(--s7));
    display: flex;
    flex-direction: column;
    gap: var(--s3);
}
.carte-corps > :deep(*) {
    margin: 0;
    max-width: var(--mesure);
}
.carte-corps :deep(a) {
    color: var(--encre-3);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.15em;
}
.carte-corps :deep(a:hover) {
    text-decoration-thickness: 2px;
}
.carte-corps :deep(h3) {
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: var(--encre);
    padding-top: var(--s1);
}
.carte-corps :deep(h3 + p) {
    margin-top: calc(-1 * var(--s1));
}
.carte-corps :deep(p) {
    color: var(--encre-2);
}
.carte-corps :deep(strong) {
    font-weight: 600;
    color: var(--encre);
}
.carte-corps :deep(ul),
.carte-corps :deep(ol) {
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--s2);
}
.carte-corps :deep(ul li) {
    position: relative;
    padding-left: 0;
    color: var(--encre-2);
}
.carte-corps :deep(ul li::before) {
    content: "";
    position: absolute;
    left: calc(-1 * var(--s4));
    top: var(--s2);
    width: var(--s2);
    height: var(--s2);
    border-radius: 50%;
    background: var(--puce);
}
.carte-corps :deep(ol) {
    counter-reset: etape;
}
.carte-corps :deep(ol li) {
    counter-increment: etape;
    position: relative;
    padding-left: 0;
    color: var(--encre-2);
}
.carte-corps :deep(ol li::before) {
    content: counter(etape);
    position: absolute;
    left: calc(-1 * var(--s7));
    top: 0;
    width: 24px;
    font-weight: 600;
    color: var(--encre-4);
    font-variant-numeric: tabular-nums;
    text-align: right;
    line-height: inherit;
}
.carte-corps :deep(dl) {
    display: flex;
    flex-direction: column;
    gap: var(--s1);
}
.carte-corps :deep(dt) {
    font-weight: 600;
    color: var(--encre);
    padding-top: var(--s2);
}
.carte-corps :deep(dt:first-child) {
    padding-top: 0;
}
.carte-corps :deep(dd) {
    margin: 0;
    color: var(--encre-2);
}
/* Questions fréquentes : un repli natif par question (details/summary,
   ouvrable au clavier par Entrée ou Espace). La question en gras, un chevron
   qui pivote à l'ouverture à la place du marqueur natif, la réponse en
   retrait de 24 px, un filet entre deux questions. */
.carte-corps :deep(details) {
    border-top: 1px solid var(--trait);
    padding: var(--s2) 0 0;
}
.carte-corps :deep(details:first-of-type) {
    border-top: 0;
    padding-top: 0;
}
.carte-corps :deep(summary) {
    cursor: pointer;
    font-weight: 600;
    color: var(--encre);
    list-style: none;
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    gap: var(--s2);
    align-items: baseline;
    padding: var(--s1) 0;
}
.carte-corps :deep(summary::-webkit-details-marker) {
    display: none;
}
.carte-corps :deep(summary::marker) {
    content: "";
}
.carte-corps :deep(summary::before) {
    content: "›";
    color: var(--encre-4);
    font-weight: 600;
    transition: transform 0.15s;
}
.carte-corps :deep(details[open] > summary::before) {
    transform: rotate(90deg);
}
.carte-corps :deep(summary:focus-visible) {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: var(--s1);
}
.carte-corps :deep(details > p) {
    margin: var(--s1) 0 var(--s2) var(--s6);
}
.carte-corps :deep(p.note) {
    border-left: var(--s1) solid var(--accent-trait);
    background: var(--accent-fond);
    padding: var(--s2) var(--s3);
    border-radius: 0 var(--rayon) var(--rayon) 0;
    color: var(--encre);
}
.carte-corps :deep(p.exemple) {
    border-left: var(--s1) solid var(--trait-2);
    padding-left: var(--s3);
    color: var(--encre-2);
    font-style: italic;
}
.carte-corps :deep(p.exemple em) {
    font-style: normal;
}

/* Tableau : une colonne par segment, en-têtes alignés, chiffres tabulaires. */
.carte-corps > :deep(.defile) {
    max-width: none;
    overflow-x: auto;
    margin-inline: calc(-1 * (var(--s5) + var(--s7))) calc(-1 * var(--s5));
    padding-inline: calc(var(--s5) + var(--s7)) var(--s5);
}
.carte-corps :deep(table) {
    width: 100%;
    min-width: 640px;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 13px;
    line-height: 20px;
    font-variant-numeric: tabular-nums;
}
.carte-corps :deep(thead th) {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--encre-3);
    padding: 0 var(--s3) var(--s2) 0;
    border-bottom: 1px solid var(--trait-2);
    vertical-align: bottom;
}
.carte-corps :deep(tbody th) {
    text-align: left;
    font-weight: 600;
    color: var(--encre);
    padding: var(--s2) var(--s3) var(--s2) 0;
    border-bottom: 1px solid var(--trait);
    vertical-align: top;
    width: 25%;
}
.carte-corps :deep(tbody td) {
    padding: var(--s2) var(--s3) var(--s2) 0;
    border-bottom: 1px solid var(--trait);
    vertical-align: top;
    color: var(--encre-2);
    width: 25%;
}
.carte-corps :deep(tbody tr:last-child th),
.carte-corps :deep(tbody tr:last-child td) {
    border-bottom: 0;
}

.pied {
    margin: var(--s2) 0 0;
    font-size: 12px;
    color: var(--encre-4);
}

@media (max-width: 900px) {
    .grille {
        grid-template-columns: minmax(0, 1fr);
    }
    .chapeau,
    .sommaire,
    .contenu {
        grid-column: auto;
        grid-row: auto;
    }
    /* P2 : le sommaire est supprimé sous 900 px (les titres des panneaux
       en tiennent lieu). */
    .sommaire {
        display: none;
    }
    .page-building-optim-turpe-v2-aide :deep(.aide-entete) {
        padding-inline-start: var(--colonne-texte);
    }
}
@media (max-width: 640px) {
    .page-building-optim-turpe-v2-aide :deep(.aide-entete),
    .chapeau {
        padding-inline-start: 0;
    }
    .page-building-optim-turpe-v2-aide :deep(.aide-entete h1) {
        font-size: 20px;
        line-height: 28px;
    }
    .carte :deep(.p-accordionheader) {
        padding: var(--s3) var(--s4);
    }
    .carte-corps {
        padding: var(--s3) var(--s4) var(--s4) calc(var(--s4) + var(--s7));
    }
    .carte-corps > :deep(.defile) {
        margin-inline: calc(-1 * (var(--s4) + var(--s7))) calc(-1 * var(--s4));
        padding-inline: calc(var(--s4) + var(--s7)) var(--s4);
    }
}
</style>
